import { MinusIcon, PlusIcon } from "@xola/icons";
import { Tooltip } from "@xola/ui-kit";
import clsx from "clsx";
import { SecondaryButton } from "common";
import React from "react";

interface ChangeAmountButtonProps {
    value: number;
    minMax: number;
    disabled: boolean;
    variant: Mode;
    size?: keyof typeof sizes;
    className?: string;
    error?: boolean;
    isTooltipDisabled?: boolean;
    minTooltipText?: string;
    maxTooltipText?: string;
    onChange: (value: number) => void;
    lockAtMax?: boolean;
}

export const ChangeAmountButton = ({
    value,
    minMax,
    disabled,
    variant,
    size = "medium",
    className,
    error,
    isTooltipDisabled,
    minTooltipText,
    maxTooltipText,
    onChange,
    lockAtMax = true,
}: ChangeAmountButtonProps) => {
    const isDecrease = variant === Mode.DECREASE;
    const icon = isDecrease ? <MinusIcon className="top-0" /> : <PlusIcon className="top-0" />;
    const dataTestId = `quantity-spinner-${isDecrease ? "decrease" : "increase"}-button`;
    const minMessage = minTooltipText ?? "The minimum quantity needed in order to book is";
    const tooltipContent = isDecrease ? (
        <span>
            {minMessage} <var data-var="minValue">{minMax}</var>
        </span>
    ) : (
        <span>
            {maxTooltipText ?? "You have exceeded the maximum capacity of"} <var data-var="maxValue">{minMax}</var>.
        </span>
    );

    const handleClick = () => {
        const newValue = isDecrease
            ? value > minMax
                ? value - 1
                : value
            : value < minMax || !lockAtMax
            ? value + 1
            : value;
        onChange(newValue);
    };

    return (
        <Tooltip
            content={tooltipContent}
            disabled={!disabled || isTooltipDisabled || (!isDecrease && maxTooltipText === "")}
            hideOnClick={false}
        >
            <SecondaryButton
                size="small"
                className={clsx("flex-shrink-0 text-gray-darker", sizes[size].button, className, {
                    "border-danger": error,
                })}
                data-testid={dataTestId}
                disabled={disabled}
                onClick={handleClick}
            >
                {icon}
            </SecondaryButton>
        </Tooltip>
    );
};

export const sizes = {
    medium: {
        button: "h-6 w-6 px-0.5",
        value: "text-base",
    },

    large: {
        button: "h-10 w-10 p-1",
        value: "text-lg font-bold",
    },
};

export enum Mode {
    DECREASE = "minus",
    INCREASE = "plus",
}
